/**
 * Square Connection card (feat-010-square-connect-admin).
 *
 * Spec: worktrees/feat-010-square-connect-admin/spec.md
 *
 * Renders the 5 connection states (not_connected, account_connected_unmapped,
 * mapped, needs_reauth, error) for one Bespoke location. The warning tier is
 * driven by connection state ONLY — elapsed time alone never triggers
 * warning styling. The 24h Healthy/Quiet boundary is presentation only and
 * is derived from the backend's `last_transaction_at` timestamp; no client
 * metric math.
 *
 * RBAC: viewable by Admin/Manager/Corporate (gated upstream by StaffRoute).
 * Connect/Map/Remove/Disconnect actions require Admin or Corporate per the
 * frontend charter.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { AlertTriangle, CheckCircle2, Clock, ExternalLink, Link2, Loader2, RefreshCw, Trash2, Unlink, X } from 'lucide-react';
import {
  ConnectionState,
  MappingConflictError,
  SquareLocation,
  SquareTransactionDetail,
  SquareTransactionRow,
  disconnectTenant,
  getConnectionState,
  getTransactionDetail,
  initiateOAuth,
  listRecentTransactions,
  listSquareLocations,
  removeLocationMapping,
  setLocationMapping,
} from '../../../services/squareConnectionService';
import {
  OrderMatchState,
  getOrderMatchState,
  rematchOrder,
  unmatchOrder,
} from '../../../services/posMatchingService';

export type UiState =
  | 'loading'
  | 'not_connected'
  | 'unmapped'
  | 'mapped'
  | 'needs_reauth'
  | 'error';

interface Props {
  bespokeLocationId: string;
  bespokeLocationName: string;
  tenantId: string;
  /** Whether the current user can perform Connect/Map/Remove/Disconnect. */
  canManage: boolean;
  /** Initial flash message (e.g. from OAuth callback redirect query param). */
  initialFlash?: { kind: 'success' | 'error'; text: string } | null;
}

function deriveUiState(s: ConnectionState | null): Exclude<UiState, 'loading' | 'error'> | null {
  if (!s) return null;
  if (s.tenant_connection.status === 'not_connected') return 'not_connected';
  if (s.tenant_connection.status === 'needs_reauth') return 'needs_reauth';
  // tenant connection is active
  if (s.location_mapping.status === 'mapped') return 'mapped';
  return 'unmapped';
}

const SquareConnectionCard: React.FC<Props> = ({
  bespokeLocationId,
  bespokeLocationName,
  tenantId,
  canManage,
  initialFlash = null,
}) => {
  const [state, setState] = useState<ConnectionState | null>(null);
  const [uiState, setUiState] = useState<UiState>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; text: string } | null>(initialFlash);
  const [busy, setBusy] = useState<null | 'connect' | 'map' | 'remove' | 'disconnect'>(null);

  const refresh = useCallback(async () => {
    setUiState('loading');
    setLoadError(null);
    try {
      const s = await getConnectionState(bespokeLocationId);
      setState(s);
      const derived = deriveUiState(s);
      setUiState(derived ?? 'error');
    } catch (e: any) {
      console.error('SquareConnectionCard - load failed', e);
      setLoadError(e?.message ?? 'Failed to load Square connection');
      setUiState('error');
    }
  }, [bespokeLocationId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onConnect = useCallback(async () => {
    setBusy('connect');
    try {
      // ADR 0001 D1.1: pass the current admin path as `return_to` so the
      // backend's callback handler can 302 us back to this exact card with a
      // `square_oauth=…` flash. Browser navigation (not fetch) hands control
      // to Square; the callback finishes the dance.
      const { redirect_url } = await initiateOAuth(bespokeLocationId, window.location.pathname);
      window.location.assign(redirect_url);
    } catch (e: any) {
      setBusy(null);
      setFlash({ kind: 'error', text: e?.message ?? 'Failed to start Square connection' });
    }
  }, [bespokeLocationId]);

  return (
    <section
      data-testid="square-connection-card"
      className="bg-white border border-softBorder rounded-xl shadow-sm overflow-hidden"
    >
      <Header
        bespokeLocationName={bespokeLocationName}
        uiState={uiState}
        state={state}
      />

      {flash && (
        <FlashBanner
          flash={flash}
          onDismiss={() => setFlash(null)}
        />
      )}

      <div className="p-5 space-y-5">
        {uiState === 'loading' && <LoadingRow />}

        {uiState === 'error' && (
          <ErrorRow message={loadError ?? 'Something went wrong.'} onRetry={refresh} />
        )}

        {uiState === 'not_connected' && (
          <NotConnectedBody
            canManage={canManage}
            busy={busy === 'connect'}
            onConnect={onConnect}
          />
        )}

        {uiState === 'needs_reauth' && (
          <NeedsReauthBody
            canManage={canManage}
            busy={busy === 'connect'}
            onReconnect={onConnect}
          />
        )}

        {uiState === 'unmapped' && state && (
          <UnmappedBody
            tenantId={tenantId}
            bespokeLocationId={bespokeLocationId}
            canManage={canManage}
            busy={busy === 'map'}
            setBusy={(v) => setBusy(v ? 'map' : null)}
            setFlash={setFlash}
            onMapped={refresh}
          />
        )}

        {uiState === 'mapped' && state && (
          <MappedBody
            tenantId={tenantId}
            bespokeLocationId={bespokeLocationId}
            connectionState={state}
            canManage={canManage}
            busy={busy}
            setBusy={setBusy}
            setFlash={setFlash}
            onChanged={refresh}
          />
        )}
      </div>
    </section>
  );
};

// ---------- Header + flash ----------

const Header: React.FC<{
  bespokeLocationName: string;
  uiState: UiState;
  state: ConnectionState | null;
}> = ({ bespokeLocationName, uiState, state }) => {
  return (
    <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-softBorder bg-cream/40">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-coral to-gold flex items-center justify-center text-lg" aria-hidden>
          ⏹
        </div>
        <div>
          <h2 className="text-lg font-display font-semibold text-ink">Square Connection</h2>
          <p className="text-xs text-inkMuted">
            POS integration for <span className="font-medium text-ink">{bespokeLocationName}</span>
          </p>
        </div>
      </div>
      <HealthBadge uiState={uiState} state={state} />
    </div>
  );
};

const FlashBanner: React.FC<{ flash: { kind: 'success' | 'error'; text: string }; onDismiss: () => void }> = ({
  flash,
  onDismiss,
}) => {
  const cls = flash.kind === 'success'
    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
    : 'bg-rose-50 border-rose-200 text-rose-900';
  return (
    <div className={`mx-5 mt-4 flex items-start justify-between gap-3 border rounded px-3 py-2 text-sm ${cls}`} role="status">
      <div>{flash.text}</div>
      <button onClick={onDismiss} aria-label="Dismiss" className="opacity-60 hover:opacity-100">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// ---------- Health badge (3 tiers, presentation-only) ----------
//
// Critical design rule (spec §"Critical design rule" + charter): warning
// styling is driven by connection state, NEVER elapsed time. A long silence
// on a low-volume tenant stays Quiet, not Warning.

type HealthTier = 'healthy' | 'quiet' | 'warning' | 'idle';

function deriveHealthTier(uiState: UiState, state: ConnectionState | null): HealthTier {
  if (uiState === 'needs_reauth' || uiState === 'unmapped' || uiState === 'not_connected') {
    return uiState === 'not_connected' ? 'idle' : 'warning';
  }
  if (uiState !== 'mapped' || !state) return 'idle';
  if (!state.last_transaction_at) return 'healthy'; // waiting for first txn — healthy waiting
  try {
    const ms = Date.now() - parseISO(state.last_transaction_at).getTime();
    return ms < 24 * 60 * 60 * 1000 ? 'healthy' : 'quiet';
  } catch {
    return 'healthy';
  }
}

const HealthBadge: React.FC<{ uiState: UiState; state: ConnectionState | null }> = ({ uiState, state }) => {
  const tier = deriveHealthTier(uiState, state);
  if (tier === 'idle') return null;

  const palettes: Record<Exclude<HealthTier, 'idle'>, { cls: string; label: string; Icon: typeof CheckCircle2 }> = {
    healthy: { cls: 'bg-emerald-50 text-emerald-900 border-emerald-200', label: 'Healthy',       Icon: CheckCircle2 },
    quiet:   { cls: 'bg-slate-50  text-slate-800  border-slate-200',    label: 'Quiet',          Icon: CheckCircle2 },
    warning: { cls: 'bg-amber-50  text-amber-900  border-amber-200',    label: 'Action needed',  Icon: AlertTriangle },
  };
  const { cls, label, Icon } = palettes[tier];
  return (
    <span
      data-testid="square-health-badge"
      data-health-tier={tier}
      className={`inline-flex items-center gap-1.5 text-xs font-medium border rounded-full px-2.5 py-1 ${cls}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  );
};

// ---------- Subviews per state ----------

const LoadingRow: React.FC = () => (
  <div className="flex items-center gap-2 text-sm text-inkMuted">
    <Loader2 className="w-4 h-4 animate-spin" />
    Loading Square connection…
  </div>
);

const ErrorRow: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-3">
    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
    <div className="flex-1">
      <p className="font-medium">We couldn't load the Square connection.</p>
      <p className="text-xs mt-0.5 opacity-80">{message}</p>
    </div>
    <button onClick={onRetry} className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 border border-amber-300 rounded bg-white hover:bg-amber-100">
      <RefreshCw className="w-3.5 h-3.5" />
      Retry
    </button>
  </div>
);

const NotConnectedBody: React.FC<{ canManage: boolean; busy: boolean; onConnect: () => void }> = ({
  canManage,
  busy,
  onConnect,
}) => (
  <div className="space-y-3">
    <p className="text-sm text-ink">
      No Square account is linked for this tenant yet. Connecting once unlocks all locations under
      this Square account — you'll pick the Square location for{' '}
      <span className="font-medium">this</span> Bespoke location in the next step.
    </p>
    <div>
      <button
        data-testid="square-connect-button"
        onClick={onConnect}
        disabled={!canManage || busy}
        className="inline-flex items-center gap-2 bg-coral text-white text-sm font-medium px-4 py-2 rounded hover:bg-coral/90 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
        Connect Square
      </button>
      {!canManage && (
        <p className="text-xs text-inkMuted mt-2">Only Admin or Corporate users can connect Square.</p>
      )}
    </div>
  </div>
);

const NeedsReauthBody: React.FC<{ canManage: boolean; busy: boolean; onReconnect: () => void }> = ({
  canManage,
  busy,
  onReconnect,
}) => (
  <div className="space-y-3">
    <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-3">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <div>
        <p className="font-medium">Square needs to be re-authorized.</p>
        <p className="text-xs mt-0.5 opacity-80">
          Your Square token has expired or was revoked. New POS data isn't flowing until you reconnect.
        </p>
      </div>
    </div>
    <div>
      <button
        data-testid="square-reconnect-button"
        onClick={onReconnect}
        disabled={!canManage || busy}
        className="inline-flex items-center gap-2 bg-coral text-white text-sm font-medium px-4 py-2 rounded hover:bg-coral/90 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
        Reconnect Square
      </button>
      {!canManage && (
        <p className="text-xs text-inkMuted mt-2">Only Admin or Corporate users can reconnect Square.</p>
      )}
    </div>
  </div>
);

const UnmappedBody: React.FC<{
  tenantId: string;
  bespokeLocationId: string;
  canManage: boolean;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  setFlash: (f: { kind: 'success' | 'error'; text: string } | null) => void;
  onMapped: () => void;
}> = ({ tenantId, bespokeLocationId, canManage, busy, setBusy, setFlash, onMapped }) => {
  const [locations, setLocations] = useState<SquareLocation[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>('');

  const load = useCallback(async () => {
    setListError(null);
    try {
      const r = await listSquareLocations(tenantId);
      setLocations(r.locations);
    } catch (e: any) {
      console.error('listSquareLocations failed', e);
      setListError(e?.message ?? 'Failed to load Square locations');
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    try {
      await setLocationMapping(bespokeLocationId, selected);
      setFlash({ kind: 'success', text: 'Square location mapped.' });
      onMapped();
    } catch (e: any) {
      if (e instanceof MappingConflictError) {
        // Keep the user's selection so they can pick something else without retyping.
        setFlash({ kind: 'error', text: `Already mapped to ${e.conflict.conflict_bespoke_location_name}.` });
      } else {
        setFlash({ kind: 'error', text: e?.message ?? 'Failed to set mapping' });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-ink">
        Square account is connected for this tenant. Choose which Square location corresponds to{' '}
        <span className="font-medium">this</span> Bespoke location.
      </p>

      {listError && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-medium">We couldn't load Square's location list.</p>
            <p className="text-xs mt-0.5 opacity-80">{listError}</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 border border-amber-300 rounded bg-white hover:bg-amber-100">
            <RefreshCw className="w-3.5 h-3.5" />
            Retry
          </button>
        </div>
      )}

      {!listError && (
        <form onSubmit={onSubmit} className="flex flex-col sm:flex-row sm:items-center gap-2">
          <select
            data-testid="square-location-select"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            disabled={!canManage || busy}
            className="flex-1 border border-softBorder rounded px-3 py-2 text-sm bg-white disabled:bg-cream"
          >
            <option value="">Select a Square location…</option>
            {locations.map((loc) => {
              const alreadyMapped = !!loc.currently_mapped_to_bespoke_location_id;
              const label = alreadyMapped
                ? `${loc.name}${loc.address ? ` — ${loc.address}` : ''} (Mapped to ${loc.currently_mapped_to_bespoke_location_name ?? 'another location'})`
                : `${loc.name}${loc.address ? ` — ${loc.address}` : ''}`;
              return (
                <option key={loc.id} value={loc.id} disabled={alreadyMapped}>
                  {label}
                </option>
              );
            })}
          </select>
          <button
            type="submit"
            data-testid="square-map-button"
            disabled={!canManage || busy || !selected}
            className="inline-flex items-center gap-2 bg-coral text-white text-sm font-medium px-4 py-2 rounded hover:bg-coral/90 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Map this location
          </button>
        </form>
      )}

      {!canManage && (
        <p className="text-xs text-inkMuted">Only Admin or Corporate users can map a Square location.</p>
      )}
    </div>
  );
};

const MappedBody: React.FC<{
  tenantId: string;
  bespokeLocationId: string;
  connectionState: ConnectionState;
  canManage: boolean;
  busy: null | 'connect' | 'map' | 'remove' | 'disconnect';
  setBusy: (b: null | 'connect' | 'map' | 'remove' | 'disconnect') => void;
  setFlash: (f: { kind: 'success' | 'error'; text: string } | null) => void;
  onChanged: () => void;
}> = ({ tenantId, bespokeLocationId, connectionState, canManage, busy, setBusy, setFlash, onChanged }) => {
  const mapping = connectionState.location_mapping;
  const [changing, setChanging] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="text-sm">
          <p className="text-inkMuted text-xs uppercase tracking-wide">Mapped to Square location</p>
          <p className="text-ink font-medium">{mapping.square_location_name ?? mapping.square_location_id}</p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="square-change-mapping-button"
              onClick={() => setChanging((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-softBorder rounded bg-white hover:bg-cream"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {changing ? 'Cancel change' : 'Change mapping'}
            </button>
            <button
              data-testid="square-remove-mapping-button"
              onClick={() => setConfirmRemove(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 border border-softBorder rounded bg-white hover:bg-cream text-rose-700"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Remove mapping
            </button>
          </div>
        )}
      </div>

      <RecencyLine lastTransactionAt={connectionState.last_transaction_at} />

      {connectionState.tenant_connection.labor_authorized === false && (
        <LaborReauthCallout
          canManage={canManage}
          busy={busy === 'connect'}
          onReauthorize={async () => {
            setBusy('connect');
            try {
              const { redirect_url } = await initiateOAuth(bespokeLocationId, window.location.pathname);
              window.location.assign(redirect_url);
            } catch (e: any) {
              setBusy(null);
              setFlash({ kind: 'error', text: e?.message ?? 'Failed to start Square re-authorization' });
            }
          }}
        />
      )}

      {changing && canManage && (
        <UnmappedBody
          tenantId={tenantId}
          bespokeLocationId={bespokeLocationId}
          canManage={canManage}
          busy={busy === 'map'}
          setBusy={(v) => setBusy(v ? 'map' : null)}
          setFlash={setFlash}
          onMapped={() => {
            setChanging(false);
            onChanged();
          }}
        />
      )}

      <RecentTransactionsPanel bespokeLocationId={bespokeLocationId} canManage={canManage} />

      {canManage && (
        <div className="pt-2 border-t border-softBorder">
          <button
            data-testid="square-disconnect-tenant-button"
            onClick={() => setConfirmDisconnect(true)}
            className="text-xs text-rose-700 hover:underline"
          >
            Disconnect Square account for this tenant…
          </button>
        </div>
      )}

      {confirmRemove && (
        <ConfirmModal
          title="Remove Square mapping?"
          body={
            <>
              <p className="text-sm text-ink">
                New Square transactions will stop flowing into this Bespoke location.
                Previously landed transactions will be kept and will still appear in reports.
                You can re-map at any time.
              </p>
            </>
          }
          confirmLabel="Remove mapping"
          danger
          busy={busy === 'remove'}
          onCancel={() => setConfirmRemove(false)}
          onConfirm={async () => {
            setBusy('remove');
            try {
              await removeLocationMapping(bespokeLocationId);
              setFlash({ kind: 'success', text: 'Mapping removed. Previously landed transactions are retained.' });
              setConfirmRemove(false);
              onChanged();
            } catch (e: any) {
              setFlash({ kind: 'error', text: e?.message ?? 'Failed to remove mapping' });
            } finally {
              setBusy(null);
            }
          }}
        />
      )}

      {confirmDisconnect && (
        <ConfirmModal
          title="Disconnect Square account?"
          body={
            <>
              <p className="text-sm text-ink">
                This revokes the Square OAuth tokens for the whole tenant and removes
                every Bespoke location's mapping. Previously landed POS data is retained.
                You'll need to reconnect Square and re-map each location to resume ingestion.
              </p>
            </>
          }
          confirmLabel="Disconnect Square"
          danger
          busy={busy === 'disconnect'}
          onCancel={() => setConfirmDisconnect(false)}
          onConfirm={async () => {
            setBusy('disconnect');
            try {
              const r = await disconnectTenant(tenantId);
              setFlash({ kind: 'success', text: `Square disconnected. ${r.mappings_disconnected} location mapping${r.mappings_disconnected === 1 ? '' : 's'} removed.` });
              setConfirmDisconnect(false);
              onChanged();
            } catch (e: any) {
              setFlash({ kind: 'error', text: e?.message ?? 'Failed to disconnect Square' });
            } finally {
              setBusy(null);
            }
          }}
        />
      )}
    </div>
  );
};

// ---------- Recency + recent-tx panel ----------

const RecencyLine: React.FC<{ lastTransactionAt: string | null }> = ({ lastTransactionAt }) => {
  if (!lastTransactionAt) {
    return (
      <p data-testid="square-recency-line" className="text-sm text-inkMuted">
        No transactions yet — waiting for your first Square sale.
      </p>
    );
  }
  let relative: string;
  try {
    relative = formatDistanceToNow(parseISO(lastTransactionAt), { addSuffix: true });
  } catch {
    relative = lastTransactionAt;
  }
  const ms = (() => {
    try { return Date.now() - parseISO(lastTransactionAt).getTime(); } catch { return 0; }
  })();
  const isQuiet = ms >= 24 * 60 * 60 * 1000;
  return (
    <div data-testid="square-recency-line" className="text-sm">
      <p className="text-ink">Last transaction received <span className="font-medium">{relative}</span>.</p>
      {isQuiet && (
        <p className="text-xs text-inkMuted mt-0.5">If this looks wrong, check your Square connection.</p>
      )}
    </div>
  );
};

const RecentTransactionsPanel: React.FC<{ bespokeLocationId: string; canManage: boolean }> = ({ bespokeLocationId, canManage }) => {
  const [rows, setRows] = useState<SquareTransactionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);
  const fetchedFor = useRef<string | null>(null);

  useEffect(() => {
    if (fetchedFor.current === bespokeLocationId) return;
    fetchedFor.current = bespokeLocationId;
    listRecentTransactions(bespokeLocationId, 10)
      .then((r) => setRows(r.rows))
      .catch((e) => {
        console.error('listRecentTransactions failed', e);
        setError(e?.message ?? 'Failed to load recent transactions');
      });
  }, [bespokeLocationId]);

  return (
    <div data-testid="square-recent-tx-panel">
      <h3 className="text-sm font-medium text-ink mb-2">Recent transactions</h3>
      <p className="text-xs text-inkMuted mb-2">
        The 10 most recent Square transactions that arrived after this location was connected.
      </p>

      {error && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {error}
        </div>
      )}

      {!error && rows === null && (
        <div className="flex items-center gap-2 text-sm text-inkMuted">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      )}

      {!error && rows !== null && rows.length === 0 && (
        <div data-testid="square-recent-tx-empty" className="rounded border border-softBorder bg-cream/40 p-3 text-sm">
          <p className="text-ink font-medium">Waiting for first transaction</p>
          <p className="text-xs text-inkMuted mt-1">
            Square transactions show up here once the connection lands data — typically within a few
            minutes of your next sale.
          </p>
        </div>
      )}

      {!error && rows !== null && rows.length > 0 && (
        <ul className="divide-y divide-softBorder border border-softBorder rounded">
          {rows.map((row) => (
            <TransactionRow key={row.id} row={row} onOpen={() => setOpenOrderId(row.id)} />
          ))}
        </ul>
      )}

      {openOrderId && (
        <ReceiptModal
          bespokeLocationId={bespokeLocationId}
          posOrderId={openOrderId}
          canManage={canManage}
          onClose={() => setOpenOrderId(null)}
        />
      )}
    </div>
  );
};

const TransactionRow: React.FC<{ row: SquareTransactionRow; onOpen: () => void }> = ({ row, onOpen }) => {
  let when: string;
  try {
    when = formatDistanceToNow(parseISO(row.occurred_at), { addSuffix: true });
  } catch {
    when = row.occurred_at;
  }
  const itemLabel = `${row.item_count} item${row.item_count === 1 ? '' : 's'}`;
  const total = `$${row.total.toFixed(2)}`;
  const pm = row.payment_method ?? '—';
  const emp = row.square_employee ?? '—';
  const item = row.first_item_name ? `'${truncate(row.first_item_name, 30)}'` : '—';
  const onKey = (e: React.KeyboardEvent<HTMLLIElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };
  return (
    <li
      data-testid="square-tx-row"
      role="button"
      tabIndex={0}
      aria-label={`Show receipt for ${total} ${itemLabel} ${when}`}
      onClick={onOpen}
      onKeyDown={onKey}
      className="grid grid-cols-12 gap-2 items-center px-3 py-2 text-xs sm:text-sm whitespace-nowrap overflow-hidden cursor-pointer hover:bg-cream/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-1"
    >
      <span className="col-span-3 text-inkMuted">{when}</span>
      <span className="col-span-2 font-medium text-ink">{total}</span>
      <span className="col-span-2 text-ink">{itemLabel}</span>
      <span className="col-span-1 text-ink">{pm}</span>
      <span className="col-span-2 text-ink truncate">{emp}</span>
      <span className="col-span-2 text-inkMuted truncate">{item}</span>
    </li>
  );
};

// ---------- Receipt modal (feat-011) ----------
//
// Paper-receipt-style detail view for one POS order. Lazily fetches the
// full payload from `/api/admin/locations/:id/square-transactions/:orderId`
// when opened (the list endpoint stays small).

const ReceiptModal: React.FC<{
  bespokeLocationId: string;
  posOrderId: string;
  canManage: boolean;
  onClose: () => void;
}> = ({ bespokeLocationId, posOrderId, canManage, onClose }) => {
  const [detail, setDetail] = useState<SquareTransactionDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getTransactionDetail(bespokeLocationId, posOrderId)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => {
        console.error('getTransactionDetail failed', e);
        if (!cancelled) setLoadError(e?.message ?? 'Failed to load receipt');
      });
    return () => { cancelled = true; };
  }, [bespokeLocationId, posOrderId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      data-testid="square-receipt-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Transaction receipt"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm max-h-[90vh] overflow-y-auto font-mono text-[13px] leading-tight text-ink p-5 relative">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-2 right-2 opacity-60 hover:opacity-100"
        >
          <X className="w-4 h-4" />
        </button>

        {!detail && !loadError && (
          <div data-testid="square-receipt-loading" className="flex items-center gap-2 text-sm text-inkMuted py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading receipt…
          </div>
        )}

        {loadError && (
          <div data-testid="square-receipt-error" className="space-y-3 py-4 text-sm">
            <p className="text-rose-700">We couldn't load this receipt.</p>
            <p className="text-xs text-inkMuted">{loadError}</p>
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="text-sm px-3 py-1.5 border border-softBorder rounded hover:bg-cream"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {detail && <ReceiptBody detail={detail} />}

        {detail && (
          <MatchStatePanel
            bespokeLocationId={bespokeLocationId}
            source="square"
            vendorOrderId={detail.vendor_order_id}
            canManage={canManage}
          />
        )}
      </div>
    </div>
  );
};

// ---------- Match-state panel (feat-039 F1) ----------
//
// Shows how this POS order was linked to a Bespoke shift + which line items
// were attributed to AI-detected upsell interactions, reusing the Revel
// order-overlay shape from ShiftTranscriptView.tsx (matched items highlighted,
// match_rule label + AI explanation). Rematch (async, 202) / Unmatch (sync,
// clears link rows) are Admin/Corporate only — gated by `canManage`.

const MATCH_RULE_LABELS: Record<string, string> = {
  metric_1: '🥩 Steak Shawarma Upsell',
  metric_2: '🍖 Döner Kebab Upsell',
  metric_3: '🍫 Dubai Chocolate Upsell',
  drink_promotion: '🥤 Drink Promotion',
};

function formatOffset(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

const MatchStatePanel: React.FC<{
  bespokeLocationId: string;
  source: 'square';
  vendorOrderId: string;
  canManage: boolean;
}> = ({ bespokeLocationId, source, vendorOrderId, canManage }) => {
  const [match, setMatch] = useState<OrderMatchState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'rematch' | 'unmatch'>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const m = await getOrderMatchState(bespokeLocationId, source, vendorOrderId);
      setMatch(m);
    } catch (e: any) {
      console.error('getOrderMatchState failed', e);
      setLoadError(e?.message ?? 'Failed to load match state');
    }
  }, [bespokeLocationId, source, vendorOrderId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await getOrderMatchState(bespokeLocationId, source, vendorOrderId);
        if (!cancelled) setMatch(m);
      } catch (e: any) {
        if (!cancelled) setLoadError(e?.message ?? 'Failed to load match state');
      }
    })();
    return () => { cancelled = true; };
  }, [bespokeLocationId, source, vendorOrderId]);

  const onRematch = async () => {
    setBusy('rematch');
    setNotice(null);
    try {
      await rematchOrder(bespokeLocationId, source, vendorOrderId);
      // Async (202): the matcher runs off the queue. Tell the operator it's
      // queued; they can reload to see the result once it lands (~30s).
      setNotice('Rematch queued — reload in a moment to see the updated match.');
    } catch (e: any) {
      setNotice(e?.message ?? 'Failed to queue rematch');
    } finally {
      setBusy(null);
    }
  };

  const onUnmatch = async () => {
    setBusy('unmatch');
    setNotice(null);
    try {
      await unmatchOrder(bespokeLocationId, source, vendorOrderId);
      setNotice('Match cleared.');
      await load();
    } catch (e: any) {
      setNotice(e?.message ?? 'Failed to clear match');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="pos-match-panel" className="mt-4 pt-3 border-t border-softBorder font-sans">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-inkMuted">Bespoke match</h4>
        {canManage && match && (
          <div className="flex items-center gap-2">
            <button
              data-testid="pos-rematch-button"
              onClick={onRematch}
              disabled={busy !== null}
              className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 border border-softBorder rounded bg-white hover:bg-cream disabled:opacity-50"
            >
              {busy === 'rematch' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
              Rematch
            </button>
            {match.matched && (
              <button
                data-testid="pos-unmatch-button"
                onClick={onUnmatch}
                disabled={busy !== null}
                className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 border border-softBorder rounded bg-white hover:bg-cream text-rose-700 disabled:opacity-50"
              >
                {busy === 'unmatch' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
                Unmatch
              </button>
            )}
          </div>
        )}
      </div>

      {notice && (
        <p data-testid="pos-match-notice" className="text-xs text-inkMuted mb-2">{notice}</p>
      )}

      {loadError && (
        <p className="text-xs text-amber-700">Couldn’t load match state: {loadError}</p>
      )}

      {!loadError && match === null && (
        <div className="flex items-center gap-2 text-xs text-inkMuted">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading match…
        </div>
      )}

      {match && !match.matched && (
        <div data-testid="pos-match-unmatched" className="rounded border border-softBorder bg-cream/40 p-3 text-xs">
          <p className="text-ink font-medium">Not yet matched to a Bespoke shift</p>
          <p className="text-inkMuted mt-1">
            This order hasn’t been linked to a recorded shift. It links automatically as shifts and
            transcripts land{canManage ? ', or use Rematch to enqueue it now' : ''}.
          </p>
        </div>
      )}

      {match && match.matched && (
        <div data-testid="pos-match-matched" className="rounded border border-emerald-200 bg-emerald-50 p-3 text-xs space-y-2">
          <div className="flex items-center gap-2 text-emerald-900">
            <Clock className="w-3.5 h-3.5" />
            <span>
              Linked at <span className="font-medium">{formatOffset(match.shift_offset_seconds ?? 0)}</span> into the shift
              {match.matched_at && (
                <> · matched {(() => { try { return formatDistanceToNow(parseISO(match.matched_at), { addSuffix: true }); } catch { return match.matched_at; } })()}</>
              )}
            </span>
          </div>

          {match.matched_items.length === 0 ? (
            <p className="text-emerald-800">
              Order linked to the shift, but no line items were attributed to an upsell interaction.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {match.matched_items.map((it) => (
                <li key={it.pos_order_line_item_id} data-testid="pos-matched-item" className="bg-yellow-100 border border-yellow-300 rounded px-2 py-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-yellow-900">
                      {it.item_name}{it.quantity > 1 ? <span className="text-yellow-700 font-normal"> ×{it.quantity}</span> : null}
                    </span>
                    <span className="text-yellow-800 font-medium shrink-0">
                      {MATCH_RULE_LABELS[it.match_rule] ?? it.match_rule}
                    </span>
                  </div>
                  <p className="text-green-800 italic mt-0.5">
                    {it.explanation || 'Attributed to AI-detected upsell interaction'}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// ---------- Labor re-authorization callout (feat-039 B6a) ----------
//
// Shown on a connected+mapped card when the merchant has not granted the
// Square TIMECARDS_READ scope (labor_authorized === false). Re-runs the same
// OAuth initiate flow, which now requests the wider scope. Without it, the
// coverage report's clock-in columns stay dark (graceful degradation, ADR D6).

const LaborReauthCallout: React.FC<{
  canManage: boolean;
  busy: boolean;
  onReauthorize: () => void;
}> = ({ canManage, busy, onReauthorize }) => (
  <div data-testid="square-labor-reauth-callout" className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-3">
    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
    <div className="flex-1">
      <p className="font-medium">Re-authorize Square to enable Labor data</p>
      <p className="text-xs mt-0.5 opacity-80">
        Orders and matching work today, but the staff coverage report (who was clocked in vs
        recording) needs an extra Square permission added recently. Re-authorizing grants it —
        nothing else changes.
      </p>
      <div className="mt-2">
        <button
          data-testid="square-labor-reauth-button"
          onClick={onReauthorize}
          disabled={!canManage || busy}
          className="inline-flex items-center gap-2 bg-coral text-white text-xs font-medium px-3 py-1.5 rounded hover:bg-coral/90 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
          Re-authorize for Labor data
        </button>
        {!canManage && (
          <p className="text-xs mt-1 opacity-80">Only Admin or Corporate users can re-authorize Square.</p>
        )}
      </div>
    </div>
  </div>
);

// Dashed separator — visually evokes the perforation between sections on a
// printed register receipt.
const ReceiptDivider: React.FC = () => (
  <div aria-hidden className="text-inkMuted select-none my-2 overflow-hidden whitespace-nowrap">
    {'-'.repeat(60)}
  </div>
);

function formatMoney(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  return `${sign}$${abs.toFixed(2)}`;
}

const ReceiptBody: React.FC<{ detail: SquareTransactionDetail }> = ({ detail }) => {
  let whenLong: string;
  try {
    whenLong = format(parseISO(detail.occurred_at), 'PPpp');
  } catch {
    whenLong = detail.occurred_at;
  }
  const paymentLine = (() => {
    const m = detail.payment.method;
    if (m === 'Card' && detail.payment.last4) {
      return `Paid: ${m} •••• ${detail.payment.last4}`;
    }
    return `Paid: ${m}`;
  })();

  return (
    <div data-testid="square-receipt-body" className="space-y-1">
      {/* Header */}
      <div className="text-center font-semibold text-base">{detail.location_name}</div>
      <div className="text-center text-xs text-inkMuted">{whenLong}</div>
      {detail.employee_name && (
        <div className="text-center text-xs text-inkMuted">Served by {detail.employee_name}</div>
      )}
      <div className="text-center text-[11px] text-inkMuted">Order {detail.vendor_order_id}</div>

      <ReceiptDivider />

      {/* Line items */}
      {detail.line_items.length === 0 ? (
        <p className="text-xs text-inkMuted text-center py-2">No line items recorded.</p>
      ) : (
        <ul className="space-y-1">
          {detail.line_items.map((it, i) => (
            <li
              key={`${it.name}-${i}`}
              data-testid="square-receipt-line-item"
              className="flex justify-between gap-3"
            >
              <span className="flex-1 pl-6 -indent-6 break-words">
                {it.quantity} × {it.name}
              </span>
              <span className="tabular-nums shrink-0">{formatMoney(it.line_total)}</span>
            </li>
          ))}
        </ul>
      )}

      <ReceiptDivider />

      {/* Totals — discount/tax/tip rows only render when non-zero */}
      <div className="space-y-0.5">
        <div className="flex justify-between gap-3">
          <span>Subtotal</span>
          <span className="tabular-nums">{formatMoney(detail.subtotal)}</span>
        </div>
        {detail.discount_total > 0 && (
          <div data-testid="square-receipt-discount" className="flex justify-between gap-3">
            <span>Discount</span>
            <span className="tabular-nums">-{formatMoney(detail.discount_total)}</span>
          </div>
        )}
        {detail.tax_total > 0 && (
          <div data-testid="square-receipt-tax" className="flex justify-between gap-3">
            <span>Tax</span>
            <span className="tabular-nums">{formatMoney(detail.tax_total)}</span>
          </div>
        )}
        {detail.tip_total > 0 && (
          <div data-testid="square-receipt-tip" className="flex justify-between gap-3">
            <span>Tip</span>
            <span className="tabular-nums">{formatMoney(detail.tip_total)}</span>
          </div>
        )}
        <div className="flex justify-between gap-3 font-semibold pt-1">
          <span>Total</span>
          <span className="tabular-nums">{formatMoney(detail.total)}</span>
        </div>
      </div>

      <ReceiptDivider />

      {/* Footer */}
      <div className="text-center">{paymentLine}</div>
    </div>
  );
};

function truncate(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max - 1) + '…';
}

// ---------- Confirmation modal ----------

const ConfirmModal: React.FC<{
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ title, body, confirmLabel, danger, busy, onCancel, onConfirm }) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-label={title}
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    onClick={(e) => {
      if (e.target === e.currentTarget && !busy) onCancel();
    }}
  >
    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-lg font-display font-semibold text-ink">{title}</h3>
        <button onClick={onCancel} disabled={busy} aria-label="Close" className="opacity-60 hover:opacity-100">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div>{body}</div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={busy}
          className="text-sm px-4 py-2 border border-softBorder rounded hover:bg-cream"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={busy}
          className={`text-sm font-medium px-4 py-2 rounded text-white inline-flex items-center gap-2 disabled:opacity-50 ${
            danger ? 'bg-rose-600 hover:bg-rose-700' : 'bg-coral hover:bg-coral/90'
          }`}
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

export default SquareConnectionCard;
